import type { Deck, Slide, SlideContent } from '@segnavia/format'
import type {
  ControllerOptions,
  DeckController,
  SegnaviaStage,
  SegnaviaThumbnail,
  StageFrameDetail,
} from '@segnavia/frame'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GalleryVerticalEnd, Minimize2, NotebookText, Play } from 'lucide-react'
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCompanionViews } from '../../lib/companion'
import { hasUnsavedChanges } from '../../lib/unsaved'
import { useViewportBand } from '../../lib/viewport'
import type { CompanionView } from '../Companion'
import { DocSlot } from '../DocSlot'
import { InfoTip } from '../InfoTip'
import { Button, Card, Field, Notice, Textarea } from '../index'
import { NavList, NavListItem, useNavListView } from '../NavList'
import { PageActionFor, useDocSlot } from '../PageAction'
import { Playback } from '../Playback'
import { Sheet } from '../Sheet'
import { useConfirm } from '../useConfirm'
import { useReorder } from '../useReorder'
import { DocFacts } from './DocFacts'
import type { DocHostClient, DocRef, LoadedDocView } from './DocView'
import {
  PendingTicketMemo,
  SlideTicketMemo,
  thumbnailSlideContent,
} from './slide-tickets'
import './slides-view.css'

/** How long the pointer rests before a presentation puts its controls away. */
const STILL_AFTER_MS = 2000

/**
 * While `active`, whether the pointer has rested: the audience sees the slide
 * alone, and moving the pointer brings the way out back.
 */
function usePointerStill(active: boolean) {
  const [still, setStill] = useState(false)
  useEffect(() => {
    if (!active) return
    let timer = window.setTimeout(() => setStill(true), STILL_AFTER_MS)
    const moved = () => {
      setStill(false)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setStill(true), STILL_AFTER_MS)
    }
    window.addEventListener('pointermove', moved)
    window.addEventListener('pointerdown', moved)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('pointermove', moved)
      window.removeEventListener('pointerdown', moved)
      setStill(false)
    }
  }, [active])
  return still
}

/** A slide's accessible name: its number, and the name the list shows. */
function slideLabel(at: number, name: string | null | undefined): string {
  return name ? `Slide ${at + 1}: ${name}` : `Slide ${at + 1}`
}

const MOVE_HELP =
  'Drag a slide, or press Alt+Arrow Up or Alt+Arrow Down to move it.'

type SlideDoc = Extract<LoadedDocView, { format: 'slides' }>
type Resolver = NonNullable<ControllerOptions['resolveSlide']>
type LoadedSlide = SlideDoc['extra']['slides'][number]

export function moveSlideBetween(
  slides: readonly LoadedSlide[],
  from: number,
  to: number,
) {
  const moving = slides[from]
  if (!moving || to < 0 || to >= slides.length || from === to) return null
  const next = [...slides]
  next.splice(from, 1)
  next.splice(to, 0, moving)
  const after = next[to - 1]?.id
  const before = next[to + 1]?.id
  return {
    slides: next,
    move: {
      id: moving.id,
      version: moving.version,
      ...(after ? { after } : {}),
      ...(before ? { before } : {}),
    },
  }
}

/** A resolver supplies the hosted content before this placeholder is shown. */
export const UNRESOLVED_SLIDE_CONTENT = {
  kind: 'url' as const,
  src: 'about:blank',
}

function saveDownload(download: { blob: Blob; filename: string }): void {
  const href = URL.createObjectURL(download.blob)
  const link = document.createElement('a')
  link.href = href
  link.download = download.filename
  link.click()
  URL.revokeObjectURL(href)
}

function DownloadAction({
  doc,
  client,
  downloads,
}: {
  doc: DocRef
  client: DocHostClient
  downloads: SlideDoc['downloads']
}) {
  const [choosing, setChoosing] = useState(false)
  const download = useMutation({
    mutationFn: (variant: string) => {
      if (!client.download) throw new Error('This deck cannot be downloaded.')
      return client.download(doc, variant)
    },
    onSuccess: (result) => {
      saveDownload(result)
      setChoosing(false)
    },
  })
  if (!client.download || !downloads?.length) return null
  const only = downloads.length === 1 ? downloads[0] : undefined
  return (
    <>
      <PageActionFor
        type="download"
        label={download.isPending ? 'Preparing PDF…' : 'Download'}
        onSelect={() => {
          if (download.isPending) return
          if (only) download.mutate(only.variant)
          else setChoosing(true)
        }}
      />
      {choosing ? (
        <Sheet title="Download deck" onClose={() => setChoosing(false)}>
          <div className="slides-view__downloads">
            {downloads.map((choice, index) => (
              <Button
                key={choice.variant}
                variant={index === 0 ? 'default' : 'secondary'}
                disabled={download.isPending}
                onClick={() => download.mutate(choice.variant)}
              >
                {choice.label}
              </Button>
            ))}
          </div>
          {download.error ? (
            <Notice tone="error">{download.error.message}</Notice>
          ) : null}
        </Sheet>
      ) : null}
      {!choosing && download.error ? (
        <Notice tone="error">{download.error.message}</Notice>
      ) : null}
    </>
  )
}

export function deckFromSlides(loaded: SlideDoc): Deck | null {
  if (loaded.extra.slides.length === 0) return null
  return {
    title: loaded.title,
    size: { width: 1280, height: 720 },
    layout: 'fixed',
    fit: 'contain',
    frame: false,
    scripts: 'refuse',
    slides: deckSlides(loaded.extra.slides),
  }
}

/** The player's rows for the deck's slides, in the order given. */
function deckSlides(slides: readonly LoadedSlide[]): Deck['slides'] {
  return slides.map((slide) => ({
    id: slide.id,
    ...(slide.notes === undefined ? {} : { notes: slide.notes }),
    content: UNRESOLVED_SLIDE_CONTENT,
  }))
}

function isControlTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      !!target.closest(
        'button, a[href], input, textarea, select, summary, [contenteditable="true"], [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [role="option"], [role="radio"], [role="switch"], [role="tab"]',
      ))
  )
}

/** A slide's ticket and the controller's copy of its content go together, so
 * the slide's next showing mints afresh and replaces the frame that failed. */
function evictSlide(
  tickets: SlideTicketMemo<SlideContent> | null,
  controller: DeckController | null,
  slideId: string,
) {
  tickets?.forget(slideId)
  controller?.forgetContent(slideId)
}

function SlideThumbnail({
  deck,
  index,
  resolveSlide,
}: {
  deck: Deck
  index: number
  resolveSlide: Resolver
}) {
  const attach = useCallback(
    (node: Element | null) => {
      if (!node) return
      const thumbnail = node as SegnaviaThumbnail
      thumbnail.deck = deck
      thumbnail.index = index
      thumbnail.resolveSlide = resolveSlide
    },
    [deck, index, resolveSlide],
  )
  return createElement('segnavia-thumbnail', { ref: attach })
}

/**
 * A deck's contents: every slide, in order, as it looks.
 *
 * The left bar's view, because moving to a slide navigates the document itself.
 * The slide on screen is marked in ink, as a reader's position is everywhere
 * else, and the list keeps it in view as the deck is paged.
 */
function SlideList({
  deck,
  index,
  resolveSlide,
  onGo,
  canWrite,
  moving,
  moveError,
  moveStatus,
  onMove,
  names,
}: {
  deck: Deck
  /** Each slide's own name, by its id; one with none is called by its number. */
  names: ReadonlyMap<string, string | null>
  index: number
  resolveSlide: Resolver
  onGo: (index: number) => void
  canWrite: boolean
  moving: boolean
  moveError: string | null
  moveStatus: string | null
  onMove: (from: number, to: number) => void
}) {
  const list = useRef<HTMLOListElement & HTMLUListElement>(null)
  const [view, setView] = useNavListView('slidesView', 'thumbnails')
  const reorder = useReorder({
    ids: deck.slides.map((slide) => slide.id),
    enabled: canWrite && !moving,
    onMove,
  })
  useEffect(() => {
    list.current?.children[index]?.scrollIntoView({ block: 'nearest' })
  }, [index])
  return (
    <div className="slide-list-wrap">
      {moveError ? <Notice tone="error">{moveError}</Notice> : null}
      {moveStatus ? (
        <span className="sr-only" role="status">
          {moveStatus}
        </span>
      ) : null}
      <NavList
        label="Slides"
        view={view}
        onViewChange={setView}
        ordered
        listRef={list}
        before={
          canWrite ? (
            <>
              <span className="sr-only" id="slide-move-help">
                {MOVE_HELP}
              </span>
              <InfoTip size="inline" label="How to reorder slides">
                {MOVE_HELP}
              </InfoTip>
            </>
          ) : null
        }
      >
        {deck.slides.map((slide, at) => (
          <NavListItem
            key={slide.id}
            {...reorder.target(at)}
            view={view}
            current={at === index}
            currentAs="true"
            number={at + 1}
            name={names.get(slide.id) ?? `Slide ${at + 1}`}
            picture={
              <SlideThumbnail
                deck={deck}
                index={at}
                resolveSlide={resolveSlide}
              />
            }
            render={(entry) => (
              <button
                type="button"
                {...entry}
                aria-label={slideLabel(at, names.get(slide.id))}
                aria-describedby={canWrite ? 'slide-move-help' : undefined}
                {...reorder.handle(at, slide.id)}
                onClick={() => onGo(at)}
              />
            )}
          />
        ))}
      </NavList>
    </div>
  )
}

function NotesSheet({
  slide,
  doc,
  client,
  onClose,
  onSaved,
}: {
  slide: LoadedSlide
  doc: DocRef
  client: DocHostClient
  onClose: () => void
  onSaved: (slide: LoadedSlide) => void
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useConfirm()
  const [opened, setOpened] = useState(slide.notes ?? '')
  const [draft, setDraft] = useState(opened)
  const [version, setVersion] = useState(slide.version)
  const [stale, setStale] = useState(false)
  const dirty = hasUnsavedChanges(opened, draft)
  const save = useMutation({
    mutationFn: () => {
      if (!client.updateSlideNotes)
        throw new Error('These notes cannot be changed.')
      return client.updateSlideNotes(doc, {
        id: slide.id,
        version,
        notes: draft.trim() ? draft : null,
      })
    },
    onSuccess: (saved) => {
      onSaved({
        id: saved.id,
        version: saved.version,
        name: slide.name,
        ...(saved.notes === null ? {} : { notes: saved.notes }),
      })
      void qc.invalidateQueries({ queryKey: client.view(doc).queryKey })
      onClose()
    },
    onError: (error) => {
      if (
        typeof error === 'object' &&
        error !== null &&
        'data' in error &&
        (error as { data?: { code?: string } }).data?.code === 'CONFLICT'
      )
        setStale(true)
    },
  })
  const close = async () => {
    if (
      dirty &&
      !(await confirm({
        title: 'Discard notes changes?',
        body: 'The notes you typed will not be saved.',
        verb: 'Discard changes',
        destructive: false,
      }))
    )
      return
    onClose()
  }
  const reload = async () => {
    const latest = await qc.fetchQuery(client.view(doc))
    if (latest.format !== 'slides') return
    const current = latest.extra.slides.find((item) => item.id === slide.id)
    if (!current) return
    const notes = current.notes ?? ''
    setOpened(notes)
    setDraft(notes)
    setVersion(current.version)
    setStale(false)
    save.reset()
    onSaved(current)
  }
  return (
    <>
      <Sheet
        title="Speaker notes"
        ariaLabel="Speaker notes"
        onClose={() => void close()}
        primary={{
          label: save.isPending ? 'Saving…' : 'Save notes',
          disabled: !dirty || save.isPending || stale,
          onSelect: () => save.mutate(),
        }}
        secondary={{ label: 'Cancel', onSelect: () => void close() }}
      >
        <Field label="Notes" optional>
          <Textarea
            rows={10}
            maxLength={20_000}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </Field>
        {stale ? (
          <Notice tone="error">
            Somebody else saved this slide, so your notes were not saved. Reload
            the notes to edit the current version.
            <Button variant="secondary" onClick={() => void reload()}>
              Reload notes
            </Button>
          </Notice>
        ) : save.error ? (
          <Notice tone="error">{save.error.message}</Notice>
        ) : null}
      </Sheet>
      {dialog}
    </>
  )
}

function DeckPlayer({
  doc,
  client,
  loaded,
  deck,
}: {
  doc: DocRef
  client: DocHostClient
  loaded: SlideDoc
  deck: Deck
}) {
  const qc = useQueryClient()
  const [slides, setSlides] = useState(loaded.extra.slides)
  const [moveError, setMoveError] = useState<string | null>(null)
  const [moveStatus, setMoveStatus] = useState<string | null>(null)
  const [notesFor, setNotesFor] = useState<string | null>(null)
  const selectedId = useRef<string | null>(deck.slides[0]?.id ?? null)
  useEffect(() => setSlides(loaded.extra.slides), [loaded.extra.slides])
  const slideNames = useMemo(
    () => new Map(slides.map((slide) => [slide.id, slide.name])),
    [slides],
  )
  const shownDeck = useMemo(
    () => ({ ...deck, slides: deckSlides(slides) }),
    [deck, slides],
  )
  const canEditNotes = loaded.extra.canWrite && !!client.updateSlideNotes
  const canMove = loaded.extra.canWrite && !!client.moveSlide
  const [frame, setFrame] = useState<
    typeof import('@segnavia/frame/browser/headless') | null
  >(null)
  const [controller, setController] = useState<DeckController | null>(null)
  const [stage, setStage] = useState<SegnaviaStage | null>(null)
  const [presentation, setPresentation] = useState<HTMLDivElement | null>(null)
  const slot = useDocSlot()
  const band = useViewportBand()
  const [playbackShown, setPlaybackShown] = useState(true)
  const [state, setState] = useState({
    index: 0,
    slide: null as Slide | null,
    presenting: false,
  })
  const [error, setError] = useState<{ index: number; message: string } | null>(
    null,
  )
  const versions = useMemo(
    () => new Map(slides.map((slide) => [slide.id, slide.version])),
    [slides],
  )
  /* The memo is the controller's, made and dropped with it: a rebuilt
     controller mounts fresh frames, and a ticket a frame has loaded is spent. */
  const tickets = useRef<SlideTicketMemo<SlideContent> | null>(null)

  useEffect(() => {
    let active = true
    void import('@segnavia/frame/browser/headless').then((loadedFrame) => {
      if (active) setFrame(loadedFrame)
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!frame) return
    let active = true
    const memo = new SlideTicketMemo<SlideContent>()
    tickets.current = memo
    const resolveSlide: Resolver = (slide) => {
      const version = versions.get(slide.id) ?? 0
      return memo.resolve(slide.id, version, () =>
        client.previewTicket(doc, slide.id).then(({ url }) => ({
          kind: 'url' as const,
          src: url,
          bridge: true as const,
          // The document is served through `frameDocument` with author
          // scripts refused; saying so is what lets the stage read a selection.
          scripts: 'refuse' as const,
          reuseKey: `${slide.id}:${version}:${memo.generation(slide.id)}`,
        })),
      )
    }
    const player = new frame.DeckController({
      resolveSlide,
      keys: 'stage',
      prefetch: true,
    })
    setController(player)
    let restoring = true
    const changed = () => {
      if (!active) return
      const next = player.state
      if (!restoring) selectedId.current = next.slide?.id ?? null
      setState({
        index: next.index,
        slide: next.slide,
        presenting: next.presenting,
      })
      setError(null)
    }
    player.addEventListener('change', changed)
    const restoreId = selectedId.current
    void player
      .load(shownDeck)
      .then(async () => {
        const restoreAt = shownDeck.slides.findIndex(
          (slide) => slide.id === restoreId,
        )
        if (restoreAt >= 0 && restoreAt !== player.state.index)
          await player.go(restoreAt)
        restoring = false
        changed()
      })
      .catch((reason: unknown) => {
        if (!active) return
        restoring = false
        setError({
          index: player.state.index,
          message:
            reason instanceof Error
              ? reason.message
              : 'The slide could not be prepared.',
        })
      })
    return () => {
      active = false
      player.removeEventListener('change', changed)
      player.destroy()
      if (tickets.current === memo) tickets.current = null
    }
  }, [client, doc, frame, shownDeck, versions])

  useEffect(() => {
    if (!stage) return
    stage.neighbors = true
    stage.controller = controller
    const mounted = (event: Event) => {
      const { slideId, content } = (event as CustomEvent<StageFrameDetail>)
        .detail
      tickets.current?.retain(slideId, content)
    }
    const released = (event: Event) => {
      const { slideId, content } = (event as CustomEvent<StageFrameDetail>)
        .detail
      tickets.current?.release(slideId, content)
    }
    const failed = (event: ErrorEvent) => {
      const failure =
        frame && event.error instanceof frame.SlideFrameError
          ? event.error
          : null
      const index = controller?.state.index ?? 0
      if (failure) evictSlide(tickets.current, controller, failure.slideId)
      // The stage's message is written for the host's developer, not the reader.
      console.error(event.message)
      // A neighbor that failed out of sight is drawn afresh when it is reached,
      // so only the slide on screen tells the reader.
      if (failure && failure.index !== index) return
      setError({ index, message: 'The slide could not be shown.' })
    }
    stage.addEventListener('segnavia-frame-mount', mounted)
    stage.addEventListener('segnavia-frame-release', released)
    stage.addEventListener('error', failed)
    return () => {
      stage.removeEventListener('segnavia-frame-mount', mounted)
      stage.removeEventListener('segnavia-frame-release', released)
      stage.removeEventListener('error', failed)
      if (stage.controller === controller) stage.controller = null
    }
  }, [controller, frame, stage])

  useEffect(() => {
    if (!controller || !stage) return
    // While presenting, the only controls on screen are the playback band's
    // buttons, so the deck's keys reach the deck from one of them too; Space
    // and Enter stay the focused button's, which they press.
    const keydown = (event: KeyboardEvent) => {
      const deckKey =
        controller.state.presenting &&
        event.key !== ' ' &&
        event.key !== 'Enter'
      if (
        (!deckKey && isControlTarget(event.target)) ||
        (event.target instanceof Node && stage.contains(event.target))
      )
        return
      if (controller.key(event.key)) event.preventDefault()
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [controller, stage])

  const showError = (index: number, reason: unknown) =>
    setError({
      index,
      message:
        reason instanceof Error
          ? reason.message
          : 'The slide could not be prepared.',
    })
  const go = useCallback(
    (index: number) => {
      if (!controller) return
      void controller.go(index).catch((reason: unknown) =>
        setError({
          index,
          message:
            reason instanceof Error
              ? reason.message
              : 'The slide could not be prepared.',
        }),
      )
    },
    [controller],
  )
  const retry = () => {
    if (!controller || !stage || !error) return
    const slideId = shownDeck.slides[error.index]?.id
    if (slideId) evictSlide(tickets.current, controller, slideId)
    void controller.go(error.index).then(
      () => setError(null),
      (reason: unknown) => showError(error.index, reason),
    )
  }
  /* Presenting covers the window in the app's own frame rather than the
     browser's full screen, so its ground and its way out are ours. */
  const stilled = usePointerStill(state.presenting)
  const togglePresent = () => {
    if (!controller || !presentation) return
    if (controller.state.presenting) controller.stopPresenting()
    else controller.present(presentation)
  }
  /* A thumbnail clears when it leaves view and resolves again when it returns,
     so its ticket is shared only while one is being minted. */
  const thumbnailResolver = useMemo<Resolver>(() => {
    const memo = new PendingTicketMemo<SlideContent>()
    return (slide) =>
      memo.resolve(slide.id, versions.get(slide.id) ?? 0, () =>
        client
          .previewTicket(doc, slide.id)
          .then(({ url }) => thumbnailSlideContent(url)),
      )
  }, [client, doc, versions])
  const move = useMutation({
    mutationFn: (
      request: NonNullable<ReturnType<typeof moveSlideBetween>> & {
        previous: readonly LoadedSlide[]
      },
    ) => {
      if (!client.moveSlide) throw new Error('These slides cannot be moved.')
      return client.moveSlide(doc, request.move)
    },
    onSuccess: (saved) => {
      setSlides((current) =>
        current.map((slide) =>
          slide.id === saved.id ? { ...slide, version: saved.version } : slide,
        ),
      )
      void qc.invalidateQueries({ queryKey: client.view(doc).queryKey })
    },
    /* The list as it stood before this move, not the last fetch: it holds any
       notes saved since, which a refused move has no claim on. */
    onError: (_error, request) => {
      setSlides([...request.previous])
      setMoveStatus(null)
      setMoveError("Couldn't move the slide. The saved order is shown.")
      void qc.invalidateQueries({ queryKey: client.view(doc).queryKey })
    },
  })
  /* Keyed on the mutation's stable parts: the result object is new on every
     render, and the companion views this feeds would be re-registered each
     time, which renders this again. */
  const { mutate: sendMove, isPending: movePending } = move
  const moveSlide = useCallback(
    (from: number, to: number) => {
      if (movePending) return
      const request = moveSlideBetween(slides, from, to)
      if (!request) return
      selectedId.current = request.move.id
      setMoveError(null)
      setMoveStatus(`Slide ${from + 1} moved to position ${to + 1}.`)
      setSlides(request.slides)
      sendMove({ ...request, previous: slides })
    },
    [movePending, sendMove, slides],
  )
  const views = useMemo<CompanionView[]>(
    () =>
      frame
        ? [
            {
              id: 'slides',
              label: 'Slides',
              place: 'nav',
              icon: <GalleryVerticalEnd size={15} />,
              render: () => (
                <SlideList
                  deck={shownDeck}
                  names={slideNames}
                  index={state.index}
                  resolveSlide={thumbnailResolver}
                  onGo={go}
                  canWrite={canMove}
                  moving={movePending}
                  moveError={moveError}
                  moveStatus={moveStatus}
                  onMove={moveSlide}
                />
              ),
            },
          ]
        : [],
    [
      canMove,
      frame,
      go,
      movePending,
      moveError,
      moveStatus,
      moveSlide,
      shownDeck,
      slideNames,
      state.index,
      thumbnailResolver,
    ],
  )
  useCompanionViews(views)
  const attachStage = useCallback((node: Element | null) => {
    setStage(node as SegnaviaStage | null)
  }, [])
  const stageElement = createElement('segnavia-stage', {
    ref: attachStage,
    className: 'slides-view__stage',
    tabIndex: 0,
    'aria-label': 'Slide stage',
  })

  const currentSlide = slides.find(
    (slide) => slide.id === (state.slide?.id ?? selectedId.current),
  )
  const notes = currentSlide?.notes?.trim() ? currentSlide.notes : null
  /* The sheet edits the slide it was opened for, whatever the deck shows
     meanwhile: a draft belongs to one slide and is never saved onto another. */
  const notesSlide = slides.find((slide) => slide.id === notesFor)
  /* In a hand the way through the deck is a bar at the foot, where the thumb
     is: shown from the start, put away by its close, and brought back from
     the Actions. */
  const atFoot = band === 'handheld' && !!slot
  const playback = (
    <Playback
      label="Slides"
      position={`${state.index + 1} of ${shownDeck.slides.length}`}
      onPrevious={() => go(state.index - 1)}
      onNext={() => go(state.index + 1)}
      canPrevious={state.index > 0}
      canNext={state.index < shownDeck.slides.length - 1}
    >
      <Button variant="quiet" onClick={togglePresent}>
        {state.presenting ? (
          <>
            <Minimize2 className="btn__icon" aria-hidden />
            Stop presenting
          </>
        ) : (
          <>
            <Play className="btn__icon" aria-hidden />
            Present
          </>
        )}
      </Button>
    </Playback>
  )
  return (
    <div
      ref={setPresentation}
      className="slides-view__frame"
      data-theme={state.presenting ? 'dark' : undefined}
      data-presenting={state.presenting ? '' : undefined}
      data-still={state.presenting && stilled ? '' : undefined}
    >
      {state.presenting ? (
        <div className="slides-view__present-bar">{playback}</div>
      ) : null}
      <div className="slides-view__stage-wrap">
        {frame ? stageElement : null}
        {error ? (
          <div className="slides-view__error">
            <Notice tone="error">{error.message}</Notice>
            <Button variant="secondary" onClick={retry}>
              Try again
            </Button>
          </div>
        ) : null}
      </div>
      {state.presenting ? null : (
        <>
          {atFoot ? (
            <>
              <PageActionFor
                type="view"
                label="Playback"
                icon={Play}
                open={playbackShown}
                onSelect={() => setPlaybackShown((was) => !was)}
              />
              {playbackShown ? (
                <DocSlot
                  kind="playback"
                  onClose={() => setPlaybackShown(false)}
                  closeLabel="Close playback"
                >
                  {playback}
                </DocSlot>
              ) : null}
            </>
          ) : (
            playback
          )}
          {notes || canEditNotes ? (
            <Card
              title="Speaker notes"
              icon={NotebookText}
              action={
                canEditNotes && currentSlide ? (
                  <Button
                    variant="quiet"
                    onClick={() => setNotesFor(currentSlide.id)}
                  >
                    {notes ? 'Edit notes' : 'Add notes'}
                  </Button>
                ) : null
              }
            >
              {notes ? (
                <p className="slides-view__notes">{notes}</p>
              ) : (
                <p className="slides-view__notes slides-view__notes--empty">
                  No speaker notes yet.
                </p>
              )}
            </Card>
          ) : null}
          {notesSlide ? (
            <NotesSheet
              slide={notesSlide}
              doc={doc}
              client={client}
              onClose={() => setNotesFor(null)}
              onSaved={(saved) =>
                setSlides((current) =>
                  current.map((slide) =>
                    slide.id === saved.id ? saved : slide,
                  ),
                )
              }
            />
          ) : null}
        </>
      )}
    </div>
  )
}

/** The complete presentation shared by every hosted slide deck. */
export function SlidesView({
  doc,
  client,
  loaded,
  factsOnDemand,
}: {
  doc: DocRef
  client: DocHostClient
  loaded: SlideDoc
  factsOnDemand: boolean
}) {
  const deck = useMemo(() => deckFromSlides(loaded), [loaded])
  return (
    <section className="slides-view" aria-label={loaded.title}>
      <DownloadAction doc={doc} client={client} downloads={loaded.downloads} />
      <DocFacts facts={loaded.facts} onDemand={factsOnDemand} />
      {deck ? (
        <DeckPlayer doc={doc} client={client} loaded={loaded} deck={deck} />
      ) : (
        <Notice>This deck has no slides yet.</Notice>
      )}
    </section>
  )
}
